import {
  Box,
  Flex,
  Text,
  Button,
  useToast,
  useDisclosure,
  Alert,
  AlertIcon,
  AlertDescription,
  Skeleton,
  Input,
  Checkbox,
  Image,
  Tooltip,
} from '@chakra-ui/react'
import { FC, memo, useCallback, useLayoutEffect, useRef, useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { ModalConfirm } from 'components/molecules'
import { optionTargetDocument } from 'components/molecules/WordMap/data'
import {
  cacheManagementServices,
  adminServices,
  companyServices,
} from 'services'
import { AxiosError } from 'axios'
import Select from 'react-select'
import { icons } from 'constant'

// ─── Types ───────────────────────────────────────────────────────────────────

interface PreviewResult {
  has_cache?: boolean
  cache_count?: number
  news_ids_with_cache?: number[]
  cache_details?: any
}

interface CompanyOption {
  company_id: number
  company_name: string
  news_id: number
  report_year?: number
  has_comparable_report?: boolean
  company_code?: string
}

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * Compare Summary Cache Tab Component
 * Consistent with SummaryCacheTab and WordCloudCacheTab patterns
 */
export const CompareSummaryCacheTab: FC = () => {
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()

  // ── Mode: report_type or news_ids ──
  const [useReportType, setUseReportType] = useState(true)

  // ── Report type state ──
  const [selectedReportType, setSelectedReportType] = useState<number | null>(
    null,
  )

  // ── News ID / company state ──
  const [baseNewsId, setBaseNewsId] = useState('')
  const [baseCompany, setBaseCompany] = useState<CompanyOption | null>(null)
  const [baseCompanyReport, setBaseCompanyReport] = useState<any>(null)
  const [isLoadingBase, setIsLoadingBase] = useState(false)
  const [selectedCompanies, setSelectedCompanies] = useState<CompanyOption[]>(
    [],
  )
  const [companyOptions, setCompanyOptions] = useState<CompanyOption[]>([])
  const [isLoadingComparison, setIsLoadingComparison] = useState(false)
  const [filterByIndustry, setFilterByIndustry] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(
    null,
  )

  // ── Shared state ──
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null)
  const [isClearing, setIsClearing] = useState(false)

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const scrollTopRef = useRef(0)

  const formik = useFormik({
    initialValues: {},
    onSubmit: async () => {},
  })

  // ─── Company fetch ────────────────────────────────────────────────────────

  const fetchComparisonCompanies = useCallback(
    async (
      params: { company_name?: string; is_same_industry?: boolean } = {},
    ) => {
      if (!baseCompany) return
      setIsLoadingComparison(true)
      try {
        const response = await companyServices.getCompareCompanyList(
          baseCompany.company_id,
          {
            company_name: params.company_name ?? '',
            report_type: baseCompanyReport?.report_type ?? 4,
            year: baseCompanyReport?.year ?? new Date().getFullYear(),
            quarter: baseCompanyReport?.quarter,
            page: 1,
            page_size: 30,
            is_same_industry: params.is_same_industry ?? filterByIndustry,
          },
        )
        const options: CompanyOption[] = (response.data ?? [])
          .filter((c: any) => c.news_id !== baseCompany.news_id)
          .map((c: any) => ({
            company_id: c.company_id,
            company_name: c.company_name,
            news_id: c.news_id ?? c.company_id,
            company_code: c.company_code ?? '',
            report_year: c.report_year,
            has_comparable_report: c.has_comparable_report,
          }))
        setCompanyOptions(options)
      } catch {
        setCompanyOptions([])
      } finally {
        setIsLoadingComparison(false)
      }
    },
    [baseCompany, filterByIndustry, baseCompanyReport],
  )

  const debouncedFetch = useCallback(
    (value: string) => {
      if (debounceTimer) clearTimeout(debounceTimer)
      const timer = setTimeout(() => {
        fetchComparisonCompanies({
          company_name: value,
          is_same_industry: filterByIndustry,
        })
      }, 300)
      setDebounceTimer(timer)
    },
    [fetchComparisonCompanies, filterByIndustry, debounceTimer],
  )

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleModeSwitch = (toReportType: boolean) => {
    setUseReportType(toReportType)
    setPreviewResult(null)
  }

  const handleSearchBase = async () => {
    if (!baseNewsId.trim()) return
    setIsLoadingBase(true)
    try {
      const response = await adminServices.getNewsDetail({
        id: baseNewsId.trim(),
      })
      if (response.data?.company_report) {
        const base: CompanyOption = {
          company_id: response.data.company_id,
          company_name: response.data.company_name,
          news_id: parseInt(baseNewsId.trim()),
          has_comparable_report: true,
        }
        setBaseCompany(base)
        setBaseCompanyReport(response.data.company_report)
        setSelectedCompanies([base])
        setPreviewResult(null)
        fetchComparisonCompanies()
      } else {
        toast({
          title: 'エラー',
          description: 'このNews IDには企業レポートがありません',
          status: 'error',
          duration: 3000,
        })
      }
    } catch {
      toast({
        title: 'エラー',
        description: 'News IDが見つかりませんでした',
        status: 'error',
        duration: 3000,
      })
    } finally {
      setIsLoadingBase(false)
    }
  }

  const handleSelectCompany = (opt: CompanyOption) => {
    if (selectedCompanies.some(c => c.news_id === opt.news_id)) {
      setSelectedCompanies(
        selectedCompanies.filter(c => c.news_id !== opt.news_id),
      )
    } else {
      if (selectedCompanies.length >= 5) {
        toast({
          title: '制限',
          description: '最大5社まで選択できます',
          status: 'warning',
          duration: 3000,
        })
        return
      }
      setSelectedCompanies([...selectedCompanies, opt])
    }
    setPreviewResult(null)
  }

  const handleRemoveCompany = (newsId: number) => {
    if (selectedCompanies[0]?.news_id === newsId) return
    setSelectedCompanies(selectedCompanies.filter(c => c.news_id !== newsId))
    setPreviewResult(null)
  }

  const handlePreview = async () => {
    // Validation
    if (useReportType && !selectedReportType) {
      toast({
        title: 'エラー',
        description: 'レポートタイプを選択してください',
        status: 'warning',
        duration: 3000,
      })
      return
    }
    if (!useReportType && selectedCompanies.length < 2) {
      toast({
        title: 'エラー',
        description: '最低2社を選択してください',
        status: 'warning',
        duration: 3000,
      })
      return
    }

    try {
      setPreviewLoading(true)
      setPreviewResult(null)

      const params = useReportType
        ? { report_type: selectedReportType! }
        : { news_ids: selectedCompanies.map(c => c.news_id) }

      const response =
        await cacheManagementServices.previewCompareSummaryCache(params)
      if (response.data) setPreviewResult(response.data)
    } catch (error) {
      if (error instanceof AxiosError) {
        toast({
          title: 'エラー',
          description: 'プレビューの取得に失敗しました',
          status: 'error',
          duration: 3000,
        })
      }
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleRefresh = async () => {
    try {
      setIsClearing(true)

      let result
      if (useReportType) {
        result = await cacheManagementServices.refreshCompareSummaryCache({
          report_type: selectedReportType!,
        })
      } else {
        result = await cacheManagementServices.refreshCompareSummaryCache({
          news_ids: selectedCompanies.map(c => c.news_id),
        })
      }

      // Handle response
      const deletedCount = result?.data?.deleted_count ?? 0
      const totalProcess =
        result?.data?.total_process ?? result?.data?.total_processed ?? 0

      if (result?.data?.task_id) {
        // Celery task response
        toast({
          title: '成功',
          description: `${deletedCount}件のキャッシュをクリアし、${totalProcess}件を再生成します。`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        })
      } else {
        // Immediate response
        toast({
          title: '成功',
          description: `${deletedCount}件のキャッシュをクリアし、${totalProcess}件を再生成しました。`,
          status: 'success',
          duration: 5000,
        })
      }

      setPreviewResult(null)
      onClose()
    } catch (error) {
      if (error instanceof AxiosError) {
        toast({
          title: 'エラー',
          description: 'キャッシュの更新に失敗しました',
          status: 'error',
          duration: 3000,
        })
      }
    } finally {
      setIsClearing(false)
    }
  }

  // ─── Select components ────────────────────────────────────────────────────

  const CustomMenuList = memo((props: any) => {
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      scrollTopRef.current = e.currentTarget.scrollTop
    }
    useLayoutEffect(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollTopRef.current
    }, [])

    return (
      <Box
        backgroundColor="#FEFEFE"
        border="1px solid #E8ECEF"
        borderRadius="12px"
        boxShadow="0 4px 12px rgba(0,0,0,0.15)"
        overflow="visible"
        pt={2}
        w="350px"
      >
        <Box
          ref={scrollRef}
          maxH="230px"
          overflowY="auto"
          sx={{
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': {
              background: '#CBD5E0',
              borderRadius: '3px',
            },
          }}
          onScroll={handleScroll}
        >
          {props.children}
        </Box>
        <Box mt={3} px={4}>
          <Text fontSize="14px">
            あと {5 - selectedCompanies.length} 社選択可能です
          </Text>
          <Button
            bg="transparent"
            border="1px solid #0084FF"
            borderRadius="12px"
            height="48px"
            my={3}
            w="full"
            onClick={() => setMenuOpen(false)}
          >
            <Text color="#0084FF">とじる</Text>
          </Button>
        </Box>
      </Box>
    )
  })

  const formatOptionLabel = (opt: CompanyOption) => {
    const isComparable = opt.has_comparable_report === true
    const displayName = opt.report_year
      ? `${opt.company_name}（${opt.report_year}）`
      : opt.company_name
    const inner = (
      <Flex
        align="center"
        cursor={isComparable ? 'pointer' : 'not-allowed'}
        opacity={isComparable ? 1 : 0.5}
        onClick={() => isComparable && handleSelectCompany(opt)}
      >
        <Image
          pr={2}
          src={
            selectedCompanies.some(c => c.news_id === opt.news_id)
              ? icons.CHECK_ON_MINUS
              : icons.CHECK_ON_PLUS
          }
        />
        <Text fontSize="14px">{displayName}</Text>
      </Flex>
    )
    if (!isComparable) {
      return (
        <Tooltip
          key={opt.company_id}
          backgroundColor="#FFFFFFE5"
          border="1px solid #E8ECEF"
          borderRadius="24px"
          color="black"
          fontSize="12px"
          label={
            <Flex align="center" h={6} justify="center" opacity={0.6}>
              同一種類・同一年度の文書がありません
            </Flex>
          }
          marginLeft="-150px"
          placement="right"
          px={4}
          textAlign="center"
        >
          {inner}
        </Tooltip>
      )
    }
    return inner
  }

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      borderRadius: '12px',
      borderColor: '#E8ECEF',
      width: '250px',
      height: '40px',
      boxShadow: '0px 5px 10px -5px #1417181A inset',
      backgroundColor: '#FEFEFE',
      '&:hover': { borderColor: '#0084FF', cursor: 'pointer' },
      paddingLeft: '8px',
    }),
    placeholder: (base: any) => ({ ...base, opacity: 0.3 }),
    input: (base: any) => ({ ...base, textAlign: 'left' }),
    valueContainer: (base: any) => ({
      ...base,
      padding: '0 !important',
      margin: 0,
    }),
    menu: (base: any) => ({
      ...base,
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      padding: '0',
      position: 'absolute',
    }),
    option: (base: any, state: any) => ({
      ...base,
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      backgroundColor: state.isSelected ? '#FEFEFE' : '#FFF',
      color: state.isDisabled ? '#999' : '#000',
      '&:hover': { backgroundColor: state.isDisabled ? '#FFF' : '#E6F2FF' },
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      color: '#3182ce',
      svg: { fill: '#3182ce' },
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: '#3182ce',
      '&:hover': { color: '#2B6CB0' },
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    clearIndicator: (base: any) => ({
      ...base,
      color: '#3182ce',
      cursor: 'pointer',
      '&:hover': { color: '#2B6CB0' },
    }),
  }

  // ─── Derived ──────────────────────────────────────────────────────────────

  const canPreview = useReportType
    ? !!selectedReportType
    : selectedCompanies.length >= 2
  const confirmTitle = `${previewResult?.cache_count ?? 0}件のキャッシュ${
    useReportType ? 'をクリア' : 'を更新'
  }しますか？元に戻せません。`

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <FormikProvider value={formik}>
      <Box>
        {/* Mode selector — same filter section pattern as other tabs */}
        <Box mb="24px">
          <Text fontSize="16px" fontWeight="600" mb="16px">
            フィルター
          </Text>

          {/* Mode toggle */}
          <Flex gap="12px" mb="20px">
            {[
              { label: 'レポートタイプで検索', value: true },
              { label: 'News IDで検索', value: false },
            ].map(opt => (
              <Button
                key={String(opt.value)}
                borderRadius="12px"
                colorScheme={useReportType === opt.value ? 'blue' : undefined}
                size="sm"
                variant={useReportType === opt.value ? 'solid' : 'outline'}
                onClick={() => handleModeSwitch(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </Flex>

          {/* ── Filter: report_type mode ── */}
          {useReportType && (
            <Box>
              <Text fontSize="14px" fontWeight="500" mb="8px">
                レポートタイプ
              </Text>
              <Flex flexWrap="wrap" gap="8px">
                {optionTargetDocument.map(
                  (opt: { label: string; value: string }) => (
                    <Button
                      key={opt.value}
                      borderRadius="12px"
                      colorScheme={
                        selectedReportType === Number(opt.value)
                          ? 'blue'
                          : undefined
                      }
                      size="sm"
                      variant={
                        selectedReportType === Number(opt.value)
                          ? 'solid'
                          : 'outline'
                      }
                      onClick={() => {
                        setSelectedReportType(Number(opt.value))
                        setPreviewResult(null)
                      }}
                    >
                      {opt.label}
                    </Button>
                  ),
                )}
              </Flex>
              <Text color="black.100" fontSize="12px" mt="4px" opacity={0.6}>
                選択したレポートタイプの比較キャッシュを検索します
              </Text>
            </Box>
          )}

          {/* ── Filter: news_ids mode ── */}
          {!useReportType && (
            <Box>
              <Text fontSize="14px" fontWeight="500" mb="8px">
                基準企業を検索 (News ID)
              </Text>
              <Flex gap="8px" mb="12px">
                <Input
                  borderRadius="12px"
                  isDisabled={isLoadingBase}
                  placeholder="News IDを入力 (例: 12345)"
                  value={baseNewsId}
                  w="250px"
                  onChange={e => setBaseNewsId(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearchBase()}
                />
                <Button
                  colorScheme="blue"
                  h="40px"
                  isDisabled={!baseNewsId.trim() || isLoadingBase}
                  isLoading={isLoadingBase}
                  px={6}
                  onClick={handleSearchBase}
                >
                  検索
                </Button>
              </Flex>

              {baseCompany && (
                <>
                  {/* Selected company chips */}
                  <Flex flexWrap="wrap" gap="8px" mb="12px">
                    {selectedCompanies.map((c, i) => (
                      <Flex
                        key={`chip_${i}`}
                        alignItems="center"
                        bg={i === 0 ? '#EBF4FF' : '#FFF'}
                        border="1px solid #E8ECEF"
                        borderRadius="12px"
                        fontSize="14px"
                        height="40px"
                        px={3}
                        py={2}
                      >
                        {c.company_code ? `${c.company_code}: ` : ''}
                        {String.fromCharCode(65 + i)}: {c.company_name}
                        {c.report_year ? ` (${c.report_year})` : ''}
                        {i !== 0 && (
                          <Image
                            cursor="pointer"
                            ml={2}
                            src={icons.CLOSE}
                            onClick={() => handleRemoveCompany(c.news_id)}
                          />
                        )}
                      </Flex>
                    ))}
                  </Flex>

                  {/* Industry filter + company select */}
                  <Flex align="center" gap="24px">
                    <Flex alignItems="center" gap={2}>
                      <Checkbox
                        isChecked={filterByIndustry}
                        sx={{
                          '.chakra-checkbox__control': {
                            w: '30px',
                            h: '30px',
                            borderRadius: '6px',
                            border: '1px solid #007BFF',
                            bg: filterByIndustry ? '#007BFF' : 'white',
                            color: 'transparent',
                            boxShadow: 'none',
                            _checked: {
                              bg: '#007BFF',
                              color: 'white',
                              border: 'none',
                              _hover: { bg: '#007BFF' },
                              _active: { bg: '#007BFF' },
                            },
                          },
                        }}
                        onChange={() => {
                          setFilterByIndustry(prev => {
                            const next = !prev
                            fetchComparisonCompanies({ is_same_industry: next })
                            return next
                          })
                        }}
                      />
                      <Text fontSize="14px" fontWeight="500">
                        同業種に絞る
                      </Text>
                    </Flex>

                    <Select
                      isClearable
                      closeMenuOnSelect={false}
                      components={{ MenuList: CustomMenuList }}
                      filterOption={null}
                      formatOptionLabel={formatOptionLabel}
                      isLoading={isLoadingComparison}
                      isOptionDisabled={opt => !opt.has_comparable_report}
                      menuIsOpen={menuOpen}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuShouldScrollIntoView={false}
                      noOptionsMessage={() => null}
                      options={companyOptions}
                      placeholder="比較する会社名を入力"
                      styles={selectStyles}
                      value={null}
                      onChange={opt => {
                        if (opt?.has_comparable_report) handleSelectCompany(opt)
                      }}
                      onInputChange={(value, { action }) => {
                        if (action !== 'input-change') return value
                        debouncedFetch(value)
                      }}
                      onMenuClose={() => setMenuOpen(false)}
                      onMenuOpen={() => {
                        setMenuOpen(true)
                        if (companyOptions.length === 0)
                          fetchComparisonCompanies()
                      }}
                    />
                  </Flex>
                </>
              )}
            </Box>
          )}
        </Box>

        {/* Action Buttons — same order/style as SummaryCacheTab */}
        <Flex gap="16px" mb="24px">
          <Button
            isDisabled={!canPreview}
            isLoading={previewLoading}
            size="md"
            variant="outline"
            onClick={handlePreview}
          >
            プレビュー
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={!previewResult?.has_cache || isClearing}
            isLoading={isClearing}
            size="md"
            onClick={onOpen}
          >
            🔄 キャッシュをクリアして更新
          </Button>
        </Flex>

        {/* Preview Results — same Alert pattern as SummaryCacheTab */}
        {previewLoading ? (
          <Skeleton borderRadius="12px" height="100px" />
        ) : previewResult ? (
          <Alert borderRadius="12px" mb="16px" status="info">
            <AlertIcon />
            <Box flex="1">
              <AlertDescription>
                {previewResult.has_cache ? (
                  <>
                    <Text fontWeight="600" mb="8px">
                      📊 クリア対象キャッシュ:{' '}
                      {previewResult.cache_count?.toLocaleString() || 0} 件
                    </Text>
                    <Text fontSize="14px">⚠️ この操作により:</Text>
                    <Text fontSize="14px" ml="16px">
                      • 対象の比較キャッシュがクリアされます
                    </Text>
                    <Text fontSize="14px" ml="16px">
                      • 元に戻すことはできません
                    </Text>
                  </>
                ) : (
                  <Text fontWeight="600">
                    📝 対象のキャッシュは存在しません
                  </Text>
                )}
              </AlertDescription>
            </Box>
          </Alert>
        ) : null}

        <ModalConfirm
          isKeepAll
          isLoading={isClearing}
          isOpen={isOpen}
          textCancel="キャンセル"
          textOK="クリア"
          title={confirmTitle}
          onClose={onClose}
          onSubmit={handleRefresh}
        />
      </Box>
    </FormikProvider>
  )
}
